import React, { useState } from 'react';

import type { EditorActions } from '@atlaskit/editor-core';

import { HelpManagerLoader, HelpManagerPlaceholder } from '@confluence/editor-help';
import { OnboardingEditorBanner } from '@confluence/onboarding-editor';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';
import type { FullPageEditorPresetAPI } from '@confluence/editor-presets/entry-points/useFullPageEditorPreset';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities';
import { fg } from '@confluence/feature-gating';

import type { FullPageEditorComponentProps } from './FullPageEditorComponent';

type EditorProps = Pick<FullPageEditorComponentProps, 'contentId' | 'version' | 'spaceKey'>;

type AdditionalProps = {
	isContentCreationPanelOpen: boolean;
	isShowingOfflineBanner: boolean;
	editorActions: EditorActions;
	onClickOpenContentCreationPanel: () => void;
	handleTitleChange: (newTitle: string) => void;
	isInCompanyHubUnpublishedState?: boolean;
	editorApi: FullPageEditorPresetAPI | undefined | null;
};

type OnboardingHelpContainerProps = EditorProps & AdditionalProps;

export const OnboardingHelpContainer = ({
	contentId,
	version,
	spaceKey,
	editorApi,
	isContentCreationPanelOpen,
	isShowingOfflineBanner,
	editorActions,
	onClickOpenContentCreationPanel,
	handleTitleChange,
}: OnboardingHelpContainerProps) => {
	const isLivePage = useIsCurrentPageLive();
	const renderServerPlaceholder = useRenderServerPlaceholder();
	const isInCompanyHub = isCompanyHubSpaceKey(spaceKey);
	/**
	 * Editor Help Center conflicts with the OnboardingEditorBanner and should be suppressed when the banner is shown
	 * The banner has queries that need to fully resolve before the banner knows if it should render itself
	 * To prevent the help center from flashing on the screen, we hide it until the banner queries and logic are complete
	 *
	 * Since we don't render the OnboardingEditorBanner on live pages, loaded state should always be true for live pages to render the Help Manager.
	 */
	const [isOnboardingBannerDataLoaded, setIsOnboardingBannerDataLoaded] =
		useState<boolean>(isLivePage);

	const openHelpDialog = () => {
		editorApi?.helpDialog?.actions.openHelp();
	};

	return (
		<>
			{renderServerPlaceholder && fg('confluence_frontend_editor_help_center_placeholder') && (
				<HelpManagerPlaceholder />
			)}
			{isOnboardingBannerDataLoaded && (
				<HelpManagerLoader
					isNCSEditor
					contentId={contentId}
					openHelpDialog={openHelpDialog}
					isContentCreationPanelOpen={isContentCreationPanelOpen}
					isCompanyHub={isCompanyHubSpaceKey(spaceKey)}
					// TODO: Add isTemplateEditor check (part of refactor of FullPageEditorWrapperComponent)
				/>
			)}
			{/* In the Company Hub editor we already show a similar banner when a user first enters
			the editor and hasn't published the hub yet,
			therefore there is no need for the OnboardingEditorBanner */}
			{!isLivePage && !isInCompanyHub && (
				<OnboardingEditorBanner
					contentId={contentId}
					isContentCreationPanelOpen={isContentCreationPanelOpen}
					isShowingOfflineBanner={isShowingOfflineBanner}
					version={version}
					spaceKey={spaceKey}
					editorActions={editorActions}
					setIsOnboardingBannerDataLoaded={setIsOnboardingBannerDataLoaded}
					onClickOpenContentCreationPanel={onClickOpenContentCreationPanel}
					handleTitleChange={handleTitleChange}
				/>
			)}
		</>
	);
};
